import React, { memo, useCallback, useContext, useMemo } from "react";
import { Tab, Nav, Navbar } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Context from "../../../store/context";
import ListenerTabs from "./ListenerTabs";

import labels from "./helpers/labels";
import SurveyModal from "./SurveyModal";
import { selectEventHandlers } from "../../../store/slices/projectSlice";

const getSelectedListenerStorageKey = ({ projectID, workflowUUID, event }) =>
  `logic-workflow-builder:selected-listener:${projectID}:${workflowUUID}:${event}`;

const SYSTEM_EVENTS = [
  "runtime:start",
  "runtime:end",
  "message:reply",
  "outbound-delivery-status",
  "milestone",
  "form-render",
  "form-submitted",
  "form-accessed",
  "component:package",
  "process:error",
];

function EventsTabs({ dispatch, state }) {
  const navigate = useNavigate();
  const { isModalSidebar } = useContext(Context);
  const eventHandlers = useSelector(selectEventHandlers);

  const params = useParams();
  const eventCounts = useMemo(
    () =>
      (state.EventListeners || []).reduce(
        (counts, listener) => ({
          ...counts,
          [listener.EventType]: (counts[listener.EventType] || 0) + 1,
        }),
        {}
      ),
    [state.EventListeners]
  );

  const handleEventSelect = useCallback(
    (key) => {
      const listeners = (state.EventListeners || [])
        .filter((listener) => listener.EventType === key)
        .sort((a, b) => a.Order - b.Order);
      const rememberedListenerUUID = sessionStorage.getItem(
        getSelectedListenerStorageKey({
          projectID: params.projectID,
          workflowUUID: params.workflowUUID,
          event: key,
        })
      );
      const listenerUUID =
        listeners.find((listener) => listener.UUID === rememberedListenerUUID)
          ?.UUID || listeners[0]?.UUID;
      if (listenerUUID) {
        navigate(
          `/library/logic/builder/${params.projectID}/workflow/${params.workflowUUID}/event/${key}/listener/${listenerUUID}`
        );
      } else {
        navigate(
          `/library/logic/builder/${params.projectID}/workflow/${params.workflowUUID}/event/${key}`
        );
      }
    },
    [navigate, params.projectID, params.workflowUUID, state.EventListeners]
  );

  return (
    <Tab.Container id="event-tabs" activeKey={params.event}>
      <div className="row">
        <div
          className={isModalSidebar ? "col-12" : "col-xl-2"}
          style={{
            height: "100%",
            overflow: "auto",
          }}
        >
          {/* Events side menu */}
          <Navbar
            expand={isModalSidebar ? false : "xl"}
            collapseOnSelect
            onSelect={handleEventSelect}
          >
            <span
              className={`fw-bold d-block ${isModalSidebar ? "" : "d-xl-none"}`}
            >
              Event: {params.event}
            </span>
            <Navbar.Toggle aria-controls="basic-navbar-nav" />
            <Navbar.Collapse>
              <Nav variant="pills" className="flex-column w-100">
                <Nav.Item className="d-none d-xl-block">
                  <b>Events</b>
                </Nav.Item>
                {SYSTEM_EVENTS.map((event) => (
                  <Nav.Item key={event}>
                    <Nav.Link
                      eventKey={event}
                      disabled={params.event === event}
                    >
                      <div className="d-flex justify-content-between align-items-start px-2">
                        <span
                          className={`${
                            params.event === event ? "text-white " : ""
                          }`}
                        >
                          {labels[event]}
                        </span>
                        <span
                          className={`badge ${
                            params.event === event
                              ? "bg-white text-dark"
                              : "bg-secondary"
                          }`}
                        >
                          {eventCounts[event] || 0}
                        </span>
                      </div>
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </Navbar.Collapse>
          </Navbar>
        </div>
        <div
          className={isModalSidebar ? "col-12" : "col-xl-10"}
          style={{
            height: "100%",
            overflow: "auto",
          }}
        >
          {/* Listeners tabs */}
          <ListenerTabs dispatch={dispatch} />
          {params.event === "form-render" && (
            <SurveyModal state={state} eventHandlers={eventHandlers} />
          )}
        </div>
      </div>
    </Tab.Container>
  );
}

export default memo(EventsTabs);
